import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCompanyInfo } from '../services/api';
import { getPrivacyDoc, getTermsDoc, LAST_UPDATED } from './legalContent';

/**
 * Public, standalone legal page (Privacy Policy / Terms of Use). Renders the
 * same regardless of authentication state. `doc` selects which document.
 */
export default function LegalPage({ doc }) {
  const { t, i18n } = useTranslation();
  const [company, setCompany] = useState(null);

  const lang = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';

  useEffect(() => {
    let cancelled = false;
    getCompanyInfo()
      .then((c) => { if (!cancelled) setCompany(c); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  const content = doc === 'terms' ? getTermsDoc(lang, company) : getPrivacyDoc(lang, company);
  const otherDoc = doc === 'terms'
    ? { to: '/privacy', label: t('legal.privacyLink') }
    : { to: '/terms', label: t('legal.termsLink') };

  const toggleLang = () => i18n.changeLanguage(lang === 'fr' ? 'en' : 'fr');

  return (
    <div className="legal-shell">
      <header className="legal-bar">
        <Link to="/" className="legal-brand">ExpertisR</Link>
        <div className="legal-bar-actions">
          <Link to="/" className="legal-back">← {t('legal.backHome')}</Link>
          <button type="button" className="lang-switch" onClick={toggleLang}>
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
      </header>

      <main className="legal-doc">
        <h1 className="legal-title">{content.title}</h1>
        <p className="legal-updated">{t('legal.updated')} {LAST_UPDATED[lang]}</p>

        {content.intro.map((para, i) => (
          <p key={`intro-${i}`} className="legal-p">{para}</p>
        ))}

        {content.sections.map((s) => (
          <section key={s.id} className="legal-section">
            <h2 className="legal-h2">{s.h}</h2>
            {(s.p || []).map((para, i) => (
              <p key={`${s.id}-p-${i}`} className="legal-p">{para}</p>
            ))}
            {s.ul && (
              <ul className="legal-ul">
                {s.ul.map((li, i) => <li key={`${s.id}-li-${i}`}>{li}</li>)}
              </ul>
            )}
            {(s.after || []).map((para, i) => (
              <p key={`${s.id}-after-${i}`} className="legal-p">{para}</p>
            ))}
          </section>
        ))}

        <footer className="legal-footer">
          <Link to={otherDoc.to}>{otherDoc.label}</Link>
          <span className="legal-footer-sep">·</span>
          <Link to="/">{t('legal.backHome')}</Link>
        </footer>
      </main>
    </div>
  );
}
